import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { addClosure, getClosures, deleteClosure, getFacilityInfo } from '../../services/facilityService';
import Button from '../Button/Button';
import FormInput from '../FormInput/FormInput';
import styles from './FacilityClosureManager.module.css';

const formatCalendarDate = (value) => {
    if (!value) return 'N/A';
    const datePart = String(value).split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) return 'N/A';
    return new Date(year, month - 1, day).toLocaleDateString();
};

const formatClosureRange = (startValue, endValue) => {
    const start = formatCalendarDate(startValue);
    const end = formatCalendarDate(endValue);
    return start === end ? start : `${start} - ${end}`;
};

const FacilityClosureManager = ({ onClosuresChange }) => {
    const [facilityId, setFacilityId] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState(''); // Only used if single day diff time, usually full day? Requirements say "block off specific dates". 
    // Usually for facility closure it's full days? Or specific times? 
    // Requirement: "dates must appear as unavailable". 
    // I'll assume standard date (full day or specific times). 
    // To match backend timestamps, let's use date inputs and assume full day (00:00 to 23:59) for now, or allow time.
    // Simplifying to Date input. Backend expects startTime/endTime timestamps.

    const [reason, setReason] = useState('');
    const [repeat, setRepeat] = useState('none'); // none, weekly, monthly
    const [repeatEndDate, setRepeatEndDate] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState('');

    const [activeClosures, setActiveClosures] = useState([]);
    const [expandedSeries, setExpandedSeries] = useState(new Set());

    useEffect(() => {
        // Fetch Facility ID
        getFacilityInfo().then(data => {
            if (data && data.id) setFacilityId(data.id);
        }).catch(console.error);
    }, []);

    const fetchClosures = useCallback(async () => {
        if (!facilityId) return;
        try {
            // Fetch current and next calendar year so future setup work appears immediately.
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const future = new Date(today.getFullYear() + 2, 0, 1);

            const data = await getClosures({
                facilityId,
                start: today.toISOString(),
                end: future.toISOString()
            });
            setActiveClosures(data);
            if (onClosuresChange) onClosuresChange();
        } catch (err) {
            console.error(err);
        }
    }, [facilityId, onClosuresChange]);

    useEffect(() => {
        if (facilityId) fetchClosures();
    }, [facilityId, fetchClosures]);

    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMsg('');

        try {
            // Construct timestamps
            // Assume full day if not specified? Let's use Date inputs and set times.
            // Requirement says "specific dates". I will assume 00:00 to 23:59:59 of that date.

            // Construct timestamps in UTC to ensure clean date boundaries (Midnight to 23:59:59 UTC)
            // This prevents timezone offsets (like EST) from pushing the end time into the next UTC day string.
            const [sYear, sMonth, sDay] = startDate.split('-').map(Number);
            const startT = new Date(Date.UTC(sYear, sMonth - 1, sDay, 0, 0, 0, 0));

            let endT;
            if (endDate) {
                const [eYear, eMonth, eDay] = endDate.split('-').map(Number);
                endT = new Date(Date.UTC(eYear, eMonth - 1, eDay, 23, 59, 59, 999));
            } else {
                // Single day defaults to same day
                endT = new Date(Date.UTC(sYear, sMonth - 1, sDay, 23, 59, 59, 999));
            }

            const payload = {
                facilityId,
                startTime: startT.toISOString(),
                endTime: endT.toISOString(),
                reason,
                repeat,
                repeatEndDate: repeatEndDate ? new Date(repeatEndDate).toISOString() : null
            };

            const result = await addClosure(payload);
            setSuccessMsg(`Closures created successfully! (${result.count} occurrences)`);
            setStartDate('');
            setEndDate('');
            setReason('');
            setRepeat('none');
            setRepeatEndDate('');
            fetchClosures();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (closure, deleteSeries = false) => {
        if (!window.confirm(deleteSeries ? 'Are you sure you want to delete the ENTIRE series?' : 'Are you sure you want to delete this closure?')) return;

        try {
            await deleteClosure(closure.id, deleteSeries);
            if (deleteSeries) {
                setActiveClosures(activeClosures.filter(c => c.batchId !== closure.batchId));
                const newExpanded = new Set(expandedSeries);
                newExpanded.delete(closure.batchId);
                setExpandedSeries(newExpanded);
            } else {
                setActiveClosures(activeClosures.filter(c => c.id !== closure.id));
            }
            if (onClosuresChange) onClosuresChange();
        } catch (err) {
            alert("Failed to delete: " + err.message);
        }
    };

    const toggleSeries = (batchId) => {
        const newExpanded = new Set(expandedSeries);
        if (newExpanded.has(batchId)) {
            newExpanded.delete(batchId);
        } else {
            newExpanded.add(batchId);
        }
        setExpandedSeries(newExpanded);
    };

    const groupedClosures = useMemo(() => {
        const groups = [];
        const seenBatches = new Set();

        activeClosures.forEach(c => {
            if (c.batchId) {
                if (!seenBatches.has(c.batchId)) {
                    seenBatches.add(c.batchId);
                    const seriesItems = activeClosures.filter(item => item.batchId === c.batchId);
                    groups.push({
                        type: 'series',
                        batchId: c.batchId,
                        reason: c.reason,
                        count: seriesItems.length,
                        items: seriesItems,
                        startDate: seriesItems[0].startTime,
                        endDate: seriesItems[seriesItems.length - 1].endTime
                    });
                }
            } else {
                groups.push({
                    type: 'single',
                    ...c
                });
            }
        });
        return groups;
    }, [activeClosures]);

    return (
        <div className={styles.container}>
            <h3>Facility Closure Management</h3>

            <form onSubmit={handleCreate} className={styles.form}>
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <FormInput label="Date (Start)" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required />
                    </div>
                    <div className={styles.formGroup}>
                        <FormInput label="Date (End) (Optional)" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate} />
                    </div>
                </div>

                <div className={styles.formGroupFull}>
                    <FormInput label="Reason" type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Maintenance, Holiday" required />
                </div>

                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label className={styles.selectLabel}>Repeat?</label>
                        <select className={styles.selectInput} value={repeat} onChange={e => setRepeat(e.target.value)}>
                            <option value="none">None</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                    </div>

                    {repeat !== 'none' && (
                        <div className={styles.formGroup}>
                            <FormInput label="Repeat Until" type="date" value={repeatEndDate} onChange={e => setRepeatEndDate(e.target.value)} min={startDate} required />
                        </div>
                    )}
                </div>

                <div className={styles.actions}>
                    <Button type="submit" disabled={loading} variant="primary">
                        {loading ? 'Processing...' : 'Block Dates'}
                    </Button>
                </div>

                {error && <p className={styles.error}>{error}</p>}
                {successMsg && <p className={styles.success}>{successMsg}</p>}
            </form>

            <div className={styles.list}>
                <div className={styles.listHeader}>
                    <h4>Upcoming Closures</h4>
                </div>
                {activeClosures.length === 0 ? <p className={styles.noData}>No upcoming closures.</p> : (
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Reason</th>
                                    <th>Type</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedClosures.map((group, idx) => (
                                    <React.Fragment key={group.type === 'series' ? `batch-${group.batchId}` : `single-${group.id}-${idx}`}>
                                        <tr className={group.type === 'series' ? styles.seriesRow : ''}>
                                            <td>
                                                {group.type === 'series' && (
                                                    <button
                                                        className={styles.expandBtn}
                                                        onClick={() => toggleSeries(group.batchId)}
                                                    >
                                                        {expandedSeries.has(group.batchId) ? '▼' : '▶'}
                                                    </button>
                                                )}
                                                {group.type === 'series'
                                                    ? formatClosureRange(group.startDate, group.endDate)
                                                    : formatClosureRange(group.startTime, group.endTime)}
                                                {group.type === 'series' && <span className={styles.itemCount}>({group.count})</span>}
                                            </td>
                                            <td>{group.reason}</td>
                                            <td>
                                                <span className={`${styles.badge} ${group.type === 'series' ? styles.seriesBadge : styles.singleBadge}`}>
                                                    {group.type === 'series' ? 'Series' : 'Single'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.actionButtons}>
                                                    <Button
                                                        onClick={() => handleDelete(group.type === 'series' ? group.items[0] : group, group.type === 'series')}
                                                        variant={group.type === 'series' ? "danger" : "ghost"}
                                                        size="sm"
                                                    >
                                                        Delete {group.type === 'series' ? 'Series' : ''}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                        {group.type === 'series' && expandedSeries.has(group.batchId) && group.items.map(item => (
                                            <tr key={`item-${item.id}`} className={styles.expandedRow}>
                                                <td style={{ paddingLeft: '2.5rem' }}>{formatClosureRange(item.startTime, item.endTime)}</td>
                                                <td>{item.reason}</td>
                                                <td><span className={styles.instanceLabel}>Instance</span></td>
                                                <td>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className={styles.deleteInstanceBtn}
                                                        onClick={() => handleDelete(item, false)}
                                                    >
                                                        Delete
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FacilityClosureManager;
